import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';

const AddLinkModal = ({ isOpen, onClose, onSuccess }) => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  
  const [formData, setFormData] = useState({
    aurl: '',
    durl: '',
    dat: new Date().toISOString().split('T')[0], // Сегодняшняя дата по умолчанию
    td: '',
    status: 0
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Подсчет количества URL доноров
  const donorUrlsCount = formData.durl.split('\n').filter(url => url.trim()).length;

  // Управляем прокруткой body при открытии/закрытии модалки
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Сброс формы при открытии модалки
  useEffect(() => {
    if (isOpen) {
      setFormData({
        aurl: '',
        durl: '',
        dat: new Date().toISOString().split('T')[0],
        td: '',
        status: 0
      });
      setError('');
      setValidationErrors({});
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Очищаем ошибку валидации для этого поля
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.aurl.trim()) {
      errors.aurl = currentLang === 'ru' ? 'URL акцептора обязателен' : 'Acceptor URL is required';
    } else if (!isValidUrl(formData.aurl)) {
      errors.aurl = currentLang === 'ru' ? 'Введите корректный URL акцептора' : 'Enter valid acceptor URL';
    }
    
    if (!formData.durl.trim()) {
      errors.durl = currentLang === 'ru' ? 'URL донора обязателен' : 'Donor URL is required';
    } else {
      // Проверяем каждый URL в textarea
      const urls = formData.durl.split('\n').filter(url => url.trim());
      if (urls.length === 0) {
        errors.durl = currentLang === 'ru' ? 'Введите хотя бы один URL донора' : 'Enter at least one donor URL';
      } else {
        for (let i = 0; i < urls.length; i++) {
          if (!isValidUrl(urls[i].trim())) {
            errors.durl = currentLang === 'ru' ? `Некорректный URL донора в строке ${i + 1}` : `Invalid donor URL at line ${i + 1}`;
            break;
          }
        }
      }
    }
    
    if (!formData.dat) {
      errors.dat = currentLang === 'ru' ? 'Дата создания обязательна' : 'Creation date is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      // Получаем список URL доноров
      const donorUrls = formData.durl.split('\n').filter(url => url.trim());
      
      // Создаем массив ссылок для отправки одним запросом
      const linksData = donorUrls.map(durl => ({
        aurl: formData.aurl,
        durl: durl.trim(),
        dat: formData.dat,
        td: formData.td,
        status: formData.status
      }));

      const response = await fetch(`${API_URL}${currentLang}/links/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ links: linksData }),
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Ошибка создания ссылок');
      }
      if (result.errors && result.errors.length > 0) {
        let errorMessage = result.message;

          errorMessage += '<br />' + result.errors.join('<br />')

        setError(errorMessage);
        onSuccess();
        return
      }
      onSuccess();
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0: return currentLang === 'ru' ? 'Ожидает проверки' : 'Pending';
      case 1: return currentLang === 'ru' ? 'Активная' : 'Active';
      case 2: return currentLang === 'ru' ? 'Не найдена' : 'Not found';
      case 99: return currentLang === 'ru' ? 'Снята' : 'Removed';
      default: return '-';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">
            {currentLang === 'ru' ? 'Добавить новые ссылки' : 'Add New Links'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md" dangerouslySetInnerHTML={{ __html: error }}/>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* URL акцептора */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {currentLang === 'ru' ? 'URL акцептора *' : 'Acceptor URL *'}
            </label>
            <input
              type="url"
              value={formData.aurl}
              onChange={(e) => handleInputChange('aurl', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                validationErrors.aurl ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={currentLang === 'ru' ? 'https://example.com/page' : 'https://example.com/page'}
            />
            {validationErrors.aurl && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.aurl}</p>
            )}
          </div>

          {/* URL донора */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {currentLang === 'ru' ? 'URL донора *' : 'Donor URL *'}
            </label>
            <textarea
              value={formData.durl}
              onChange={(e) => handleInputChange('durl', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 resize-vertical ${
                validationErrors.durl ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={currentLang === 'ru' ? 'https://donor-site1.com/link\nhttps://donor-site2.com/link\nhttps://donor-site3.com/link' : 'https://donor-site1.com/link\nhttps://donor-site2.com/link\nhttps://donor-site3.com/link'}
              rows={4}
            />
            <div className="mt-1 flex justify-between items-center">
              <p className="text-xs text-gray-500">
                {currentLang === 'ru' ? 'Каждый URL с новой строки. Будет создана отдельная ссылка для каждого URL.' : 'Each URL on a new line. A separate link will be created for each URL.'}
              </p>
              {donorUrlsCount > 0 && (
                <span className="text-xs font-medium text-brand-600">
                  {currentLang === 'ru' ? `${donorUrlsCount} ссыл${donorUrlsCount === 1 ? 'ка' : donorUrlsCount < 5 ? 'ки' : 'ек'}` : `${donorUrlsCount} link${donorUrlsCount !== 1 ? 's' : ''}`}
                </span>
              )}
            </div>
            {validationErrors.durl && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.durl}</p>
            )}
          </div>

          {/* Статус */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {currentLang === 'ru' ? 'Статус' : 'Status'}
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleInputChange('status', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value={0}>{currentLang === 'ru' ? 'Ожидает проверки' : 'Pending'}</option>
              <option value={1}>{currentLang === 'ru' ? 'Активная' : 'Active'}</option>
              <option value={2}>{currentLang === 'ru' ? 'Не найдена' : 'Not found'}</option>
              <option value={99}>{currentLang === 'ru' ? 'Снята' : 'Removed'}</option>
            </select>
          </div>

          {/* Теги */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {currentLang === 'ru' ? 'Теги' : 'Tags'}
            </label>
            <input
              type="text"
              value={formData.td}
              onChange={(e) => handleInputChange('td', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder={currentLang === 'ru' ? 'SEO, маркетинг, ссылки' : 'SEO, marketing, links'}
            />
            <p className="mt-1 text-xs text-gray-500">
              {currentLang === 'ru' ? 'Разделяйте теги запятыми' : 'Separate tags with commas'}
            </p>
          </div>

          {/* Дата создания */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {currentLang === 'ru' ? 'Дата создания *' : 'Creation Date *'}
            </label>
            <input
              type="date"
              value={formData.dat}
              onChange={(e) => handleInputChange('dat', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                validationErrors.dat ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {validationErrors.dat && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.dat}</p>
            )}
          </div>



          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-brand-500 text-white rounded-md hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {currentLang === 'ru' ? 'Создание...' : 'Creating...'}
                </div>
              ) : (
                currentLang === 'ru' ? 'Создать ссылки' : 'Create Links'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddLinkModal; 